import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { validateEmail } from 'utils/validator'

import { Input2 } from '../Input2/Input2'

/**
 * Edit a list of email addresses, one row each.
 *
 * Deliberately a list rather than one delimited text box: a single field holding `a@x.com;b@y.com`
 * fails validation everywhere downstream, and the send path drops such a recipient without a word.
 * One address per row keeps every value individually valid, and lets the error point at the address
 * that is actually wrong.
 *
 * Pasting several addresses at once is normal here (they usually come from another system, which
 * separates them with a semicolon or comma), so a delimited value is split into rows when the field
 * is left. Splitting on every keystroke instead would tear a row in half mid-word the moment the
 * typist reached the second address.
 */
export const EmailListInput = ({
  title = 'Emails',
  value = [],
  disabled = false,
  readOnly = false,
  tooltip = '',
  placeholder = 'No additional emails',
  onChange,
  onBlur,
}: {
  title?: string
  value?: string[]
  disabled?: boolean
  readOnly?: boolean
  tooltip?: string
  placeholder?: string
  onChange: Function
  onBlur?: Function
}) => {
  // Kept locally so a row being typed into can be empty or half-written without the parent
  // dropping it. Committed upward on change; blanks are stripped when the field is left.
  const [rows, setRows] = useState<string[]>(value || [])

  useEffect(() => {
    setRows(value || [])
  }, [JSON.stringify(value || [])])

  const commit = (next: string[]) => {
    setRows(next)
    onChange(next)
  }

  const onChangeRow = (idx: number, input: string) => {
    const next = [...rows]
    next[idx] = input
    commit(next)
  }

  const onRemove = (idx: number) => {
    const next = [...rows]
    next.splice(idx, 1)
    commit(next)
    onBlur && onBlur()
  }

  const onAdd = () => commit([...rows, ''])

  const onBlurRow = () => {
    // One row per address: a value holding several (pasted, or typed with a semicolon) becomes
    // several rows, and blank rows are dropped.
    const cleaned = rows.flatMap((email) =>
      email
        .split(/[;,]/)
        .map((part) => part.trim())
        .filter((part) => part.length > 0),
    )
    if (cleaned.length !== rows.length || cleaned.some((email, i) => email !== rows[i])) {
      setRows(cleaned)
      onChange(cleaned)
    }
    onBlur && onBlur()
  }

  return (
    <div className="grid gap-2">
      <div className="flex justify-between items-center">
        <p className="font-bold text-[13px] text-gray-600" title={tooltip}>
          {title}
        </p>
        {!disabled && !readOnly && (
          <span className="btn-icon text-shade-blue" onClick={onAdd}>
            <PlusIcon className="w-4 h-4" />
          </span>
        )}
      </div>
      {rows.length === 0 && <p className="text-[13px] text-gray-400 italic">{placeholder}</p>}
      {rows.map((email, idx) => (
        <Input2
          key={`additional-email-${idx}`}
          // Input2 renders additionalElements inside its floating label, so each row needs a title
          // for the delete control to sit beside something instead of hovering over empty space.
          title={`${idx + 1}.`}
          type="email"
          value={email}
          disabled={disabled}
          readOnly={readOnly}
          error={email && !validateEmail(email) ? 'Email address is not in a valid format.' : ''}
          onChange={(v: string) => onChangeRow(idx, v)}
          onBlur={onBlurRow}
          onBlurNull={false}
          additionalElements={() =>
            disabled || readOnly ? null : (
              <TrashIcon className="w-4 h-4 cursor-pointer text-red-500" onClick={() => onRemove(idx)} />
            )
          }
        />
      ))}
    </div>
  )
}
